import { ApiResponse, Case, CaseCategory, CaseCategoryFormData, CaseChecklistItem, CaseFilters, CaseFormData, CaseInstitution, CaseInstitutionFormData, CaseTemplate, CaseTemplateFormData } from '../types';
import api from './apiClient';

export const remindersAPI = {
  getSettings: async (): Promise<any> => {
    const response = await api.get('/reminders/settings');
    return response.data;
  },
  updateSettings: async (settings: any): Promise<any> => {
    const response = await api.put('/reminders/settings', settings);
    return response.data;
  },
  getList: async (entityType: 'tasks'|'cases'|'payables', entityId: number): Promise<any> => {
    const response = await api.get('/reminders', { params: { entityType, entityId } });
    return response.data;
  },
  create: async (data: { entityType: 'tasks'|'cases'|'payables'|'meetings'|'custom'; entityId: number; remindType: 'before'|'at'|'after'; leadValue: number; leadUnit: 'minutes'|'hours'|'days'; channel?: string; fireAt?: string }): Promise<any> => {
    const response = await api.post('/reminders', data);
    return response.data;
  },
  update: async (id: number, patch: any): Promise<any> => {
    const response = await api.put(`/reminders/${id}`, patch);
    return response.data;
  },
  delete: async (id: number): Promise<any> => {
    const response = await api.delete(`/reminders/${id}`);
    return response.data;
  },
  runNow: async (): Promise<any> => {
    const response = await api.post('/reminders/run');
    return response.data;
  }
};

// Push API
export const pushAPI = {
  getPublicKey: async (): Promise<{ success?: boolean; publicKey?: string; configured?: boolean }> => {
    const response = await api.get('/push/public-key');
    return response.data;
  },
  subscribe: async (subscription: any): Promise<any> => {
    const response = await api.post('/push/subscribe', subscription);
    return response.data;
  },
  unsubscribe: async (endpoint: string): Promise<any> => {
    const response = await api.post('/push/unsubscribe', { endpoint });
    return response.data;
  }
};


export const analyticsAPI = {
  // Postęp zadań - dziennie/tygodniowo/miesięcznie
  getProgress: async (period: 'day' | 'week' | 'month' = 'week', range: number = 30) => {
    try {
      const response = await api.get(`/analytics/progress?period=${period}&range=${range}`);
      return { success: true, data: response.data.data, period, range };
    } catch (error) {
      console.error('Błąd pobierania danych postępu:', error);
      return { success: false, error: 'Błąd pobierania danych postępu' };
    }
  },

  // Heatmapa aktywności (365 dni)
  getHeatmap: async (year?: number) => {
    try {
      const currentYear = year || new Date().getFullYear();
      const response = await api.get(`/analytics/heatmap?year=${currentYear}`);
      return { success: true, ...response.data };
    } catch (error) {
      console.error('Błąd pobierania heatmapy:', error);
      return { success: false, error: 'Błąd pobierania heatmapy' };
    }
  },

  // Analiza czasu spędzonego nad zadaniami
  getTimeAnalysis: async () => {
    try {
      const response = await api.get('/analytics/time-analysis');
      return { success: true, data: response.data.data };
    } catch (error) {
      console.error('Błąd analizy czasu:', error);
      return { success: false, error: 'Błąd analizy czasu' };
    }
  },

  // Smart insights i rekomendacje
  getInsights: async () => {
    try {
      const response = await api.get('/analytics/insights');
      return { success: true, ...response.data };
    } catch (error) {
      console.error('Błąd pobierania insights:', error);
      return { success: false, error: 'Błąd pobierania insights' };
    }
  },

  // Cele i osiągnięcia
  getGoals: async () => {
    try {
      const response = await api.get('/analytics/goals');
      return { success: true, ...response.data };
    } catch (error) {
      console.error('Błąd pobierania celów:', error);
      return { success: false, error: 'Błąd pobierania celów' };
    }
  },

  // Eksport danych
  exportData: async (format: 'json' | 'csv' = 'json', period: number = 30) => {
    try {
      const response = await api.get(`/analytics/export?format=${format}&period=${period}`, {
        responseType: format === 'csv' ? 'blob' : 'json'
      });
      
      if (format === 'csv') {
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `zadania-analytics-${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
        return { success: true, message: 'Plik CSV został pobrany' };
      } else {
        return { success: true, data: response.data };
      }
    } catch (error) {
      console.error('Błąd eksportu danych:', error);
      return { success: false, error: 'Błąd eksportu danych' };
    }
  }
};

// 🏛️ CASES API - Moduł Spraw
export const casesAPI = {
  normalizeCollectionResponse: <T>(payload: any, key: string): ApiResponse<{ [k: string]: T[] }> => {
    if (Array.isArray(payload)) {
      return {
        success: true,
        message: 'OK',
        data: { [key]: payload } as { [k: string]: T[] },
        [key]: payload
      } as ApiResponse<{ [k: string]: T[] }>;
    }

    if (payload?.success !== undefined) {
      return {
        ...payload,
        data: payload.data || { [key]: payload[key] || [] },
        [key]: payload[key] || payload.data?.[key] || []
      };
    }

    return {
      success: true,
      message: 'OK',
      data: { [key]: payload?.[key] || [] } as { [k: string]: T[] },
      [key]: payload?.[key] || []
    } as ApiResponse<{ [k: string]: T[] }>;
  },

  normalizeEntityResponse: <T>(payload: any, key: string): ApiResponse<{ [k: string]: T }> => {
    if (payload?.success !== undefined) {
      return {
        ...payload,
        data: payload.data || { [key]: payload[key] || payload },
        [key]: payload[key] || payload.data?.[key] || payload
      };
    }

    return {
      success: true,
      message: 'OK',
      data: { [key]: payload } as { [k: string]: T },
      [key]: payload
    } as ApiResponse<{ [k: string]: T }>;
  },

  // Sprawy
  getCases: async (filters?: Partial<CaseFilters>): Promise<ApiResponse<{ cases: Case[] }>> => {
    const response = await api.get('/cases', { params: filters });
    return casesAPI.normalizeCollectionResponse<Case>(response.data, 'cases') as ApiResponse<{ cases: Case[] }>;
  },

  getCase: async (id: number): Promise<ApiResponse<{ case: Case }>> => {
    const response = await api.get(`/cases/${id}`);
    return casesAPI.normalizeEntityResponse<Case>(response.data, 'case') as ApiResponse<{ case: Case }>;
  },

  createCase: async (data: CaseFormData): Promise<ApiResponse<{ case: Case }>> => {
    const response = await api.post('/cases', data);
    return casesAPI.normalizeEntityResponse<Case>(response.data, 'case') as ApiResponse<{ case: Case }>;
  },

  updateCase: async (id: number, data: Partial<CaseFormData>): Promise<ApiResponse<{ case: Case }>> => {
    const response = await api.put(`/cases/${id}`, data);
    return casesAPI.normalizeEntityResponse<Case>(response.data, 'case') as ApiResponse<{ case: Case }>;
  },

  deleteCase: async (id: number): Promise<ApiResponse> => {
    const response = await api.delete(`/cases/${id}`);
    return response.data;
  },

  // Kategorie spraw
  getCaseCategories: async (): Promise<ApiResponse<{ categories: CaseCategory[] }>> => {
    const response = await api.get('/case-categories');
    return casesAPI.normalizeCollectionResponse<CaseCategory>(response.data, 'categories') as ApiResponse<{ categories: CaseCategory[] }>;
  },

  createCaseCategory: async (data: CaseCategoryFormData): Promise<ApiResponse<{ category: CaseCategory }>> => {
    const response = await api.post('/case-categories', data);
    return casesAPI.normalizeEntityResponse<CaseCategory>(response.data, 'category') as ApiResponse<{ category: CaseCategory }>;
  },

  updateCaseCategory: async (id: number, data: CaseCategoryFormData): Promise<ApiResponse<{ category: CaseCategory }>> => {
    const response = await api.put(`/case-categories/${id}`, data);
    return casesAPI.normalizeEntityResponse<CaseCategory>(response.data, 'category') as ApiResponse<{ category: CaseCategory }>;
  },

  deleteCaseCategory: async (id: number): Promise<ApiResponse> => {
    const response = await api.delete(`/case-categories/${id}`);
    return response.data;
  },

  mergeCaseCategories: async (sourceId: number, targetId: number): Promise<ApiResponse<{ movedCases: number }>> => {
    const response = await api.post(`/case-categories/${sourceId}/merge/${targetId}`);
    return response.data;
  },

  // Instytucje
  getCaseInstitutions: async (): Promise<ApiResponse<{ institutions: CaseInstitution[] }>> => {
    const response = await api.get('/case-institutions');
    return casesAPI.normalizeCollectionResponse<CaseInstitution>(response.data, 'institutions') as ApiResponse<{ institutions: CaseInstitution[] }>;
  },

  searchCaseInstitutions: async (query: string): Promise<ApiResponse<{ institutions: CaseInstitution[] }>> => {
    const response = await api.get(`/case-institutions/search/${encodeURIComponent(query)}`);
    return response.data;
  },

  createCaseInstitution: async (data: CaseInstitutionFormData): Promise<ApiResponse<{ institution: CaseInstitution }>> => {
    const response = await api.post('/case-institutions', data);
    return casesAPI.normalizeEntityResponse<CaseInstitution>(response.data, 'institution') as ApiResponse<{ institution: CaseInstitution }>;
  },

  updateCaseInstitution: async (id: number, data: CaseInstitutionFormData): Promise<ApiResponse<{ institution: CaseInstitution }>> => {
    const response = await api.put(`/case-institutions/${id}`, data);
    return casesAPI.normalizeEntityResponse<CaseInstitution>(response.data, 'institution') as ApiResponse<{ institution: CaseInstitution }>;
  },

  deleteCaseInstitution: async (id: number): Promise<ApiResponse> => {
    const response = await api.delete(`/case-institutions/${id}`);
    return response.data;
  },

  mergeCaseInstitutions: async (sourceId: number, targetId: number): Promise<ApiResponse<{ movedCases: number }>> => {
    const response = await api.post(`/case-institutions/${sourceId}/merge/${targetId}`);
    return response.data;
  },

  // Szablony spraw
  getCaseTemplates: async (includeSystem: boolean = true): Promise<ApiResponse<{ templates: CaseTemplate[] }>> => {
    const response = await api.get('/case-templates', {
      params: { include_system: includeSystem.toString() }
    });
    return response.data;
  },

  getCaseTemplate: async (id: number): Promise<ApiResponse<{ template: CaseTemplate }>> => {
    const response = await api.get(`/case-templates/${id}`);
    return response.data;
  },

  createCaseTemplate: async (data: CaseTemplateFormData): Promise<ApiResponse<{ template: CaseTemplate }>> => {
    const response = await api.post('/case-templates', data);
    return response.data;
  },

  updateCaseTemplate: async (id: number, data: CaseTemplateFormData): Promise<ApiResponse<{ template: CaseTemplate }>> => {
    const response = await api.put(`/case-templates/${id}`, data);
    return response.data;
  },

  deleteCaseTemplate: async (id: number): Promise<ApiResponse> => {
    const response = await api.delete(`/case-templates/${id}`);
    return response.data;
  },

  createCaseFromTemplate: async (templateId: number, customData?: Partial<CaseFormData>): Promise<ApiResponse<{ case: Case }>> => {
    const response = await api.post(`/case-templates/${templateId}/create-case`, customData || {});
    return response.data;
  },

  duplicateCaseTemplate: async (templateId: number): Promise<ApiResponse<{ template: CaseTemplate }>> => {
    const response = await api.post(`/case-templates/${templateId}/duplicate`);
    return response.data;
  },

  // Zarządzanie checklistą spraw
  getCaseChecklist: async (caseId: number): Promise<ApiResponse<{ checklist: CaseChecklistItem[] }>> => {
    const response = await api.get(`/case-checklists/${caseId}`);
    return response.data;
  },

  createChecklistItem: async (caseId: number, data: { title: string; description?: string }): Promise<ApiResponse<{ checklistItem: CaseChecklistItem }>> => {
    const response = await api.post(`/case-checklists/${caseId}`, data);
    return response.data;
  },

  updateChecklistItem: async (caseId: number, itemId: number, data: { title?: string; description?: string; completed?: boolean }): Promise<ApiResponse<{ checklistItem: CaseChecklistItem }>> => {
    const response = await api.put(`/case-checklists/${caseId}/${itemId}`, data);
    return response.data;
  },

  toggleChecklistItem: async (caseId: number, itemId: number): Promise<ApiResponse<{ checklistItem: CaseChecklistItem }>> => {
    const response = await api.patch(`/case-checklists/${caseId}/${itemId}/toggle`);
    return response.data;
  },

  deleteChecklistItem: async (caseId: number, itemId: number): Promise<ApiResponse> => {
    const response = await api.delete(`/case-checklists/${caseId}/${itemId}`);
    return response.data;
  },

  reorderChecklist: async (caseId: number, items: Array<{ id: number; order_index: number }>): Promise<ApiResponse<{ checklist: CaseChecklistItem[] }>> => {
    const response = await api.patch(`/case-checklists/${caseId}/reorder`, { items });
    return response.data;
  }
};
